import React, { useState, useRef, useEffect } from "react";
import {
  FetchAllServices,
  getUsersByServiceId,
} from "../../services/PackageList/PackageService";
import {
  Inbox,
  Loader,
  Loader2,
  LoaderPinwheel,
  Server,
  X,
} from "lucide-react";

function GetUsersByServiceId() {
  const [searchQuery, setSearchQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [results, setResults] = useState(null);
  const [userList, setUserList] = useState([]);
  const [users, setUsers] = useState([]); // original data
  const [loading, setLoading] = useState(false);
  const [userLoading, setUserLoading] = useState(false);
  const [selectedSevice, setSelectedService] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);

  // Mock data to demonstrate the dropdown results
  //   const mockResults = [
  //     { id: 1, name: 'Arjun Sharma', email: 'arjun.sharma@example.com', role: 'Premium User', status: 'Active' },
  //     { id: 2, name: 'Priya Patel', email: 'priya.p@example.com', role: 'Standard User', status: 'Active' },
  //     { id: 3, name: 'Rohan Das', email: 'rohan.das@example.com', role: 'Enterprise Admin', status: 'Pending' },
  //   ];

  const getUsers = async () => {
    setUserLoading(true);
    try {
      const res = await getUsersByServiceId({
        service_id: selectedSevice.id,
        page,
      });

      if (res?.status?.code === 200) {
        setUserList(res?.data);
        setUsers(res?.data);
        setPagination(res?.pagination);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setUserLoading(false);
    }
  };

  const handleFilter = (e) => {
    const value = e.target.value.toLowerCase().trim();

    if (!value) {
      setUserList(users);
      return;
    }

    const filtered = users.filter((item) => {
      return (
        item.company_name?.toLowerCase().includes(value) ||
             item.user_id?.toLowerCase().includes(value) ||
        item?.email?.toLowerCase()?.includes(value)
      );
    });

    // console.log(typeof value, value);
  
    setUserList(filtered);
  };

  useEffect(() => {
    if (!selectedSevice) return;

    getUsers();
  }, [selectedSevice, page]);

  useEffect(() => {
    if (!debouncedSearch) return;

    setLoading(true);

    const getSevices = async () => {
      try {
        const res = await FetchAllServices({
          search: debouncedSearch,
          userId: "APID1025",
        });

        if (res?.status?.code === 200) {
          setResults(res?.data);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
    getSevices();
  }, [debouncedSearch]);

  useEffect(() => {
    const timeOut = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);

    return () => {
      clearTimeout(timeOut);
    };
  }, [searchQuery]);

  return (
    <div className="min-h-screen bg-slate-50   font-sans">
      {/* Immersive mesh background accents at the top */}

      {/* Top Header & Search Bar Wrapper */}
      <header className="relative z-10 w-full border-b border-slate-200/60 bg-white/40 backdrop-blur-md">
        <div className="max-w-5xl mx-auto px-4 py-6 sm:py-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          {/* Section Branding/Title */}
          <div>
            <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-slate-800 to-slate-600 tracking-tight">
              Get The User List Service Wise
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 font-medium mt-0.5">
              Search profiles instantly by entering a service name.
            </p>
          </div>

          {/* Core Search & Dropdown Architecture */}
          <div className="relative w-full md:max-w-xl group">
            <div className="flex items-center relative">
              {/* Search Icon */}
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10">
                <svg
                  className="w-5 h-5 text-slate-400 group-focus-within:text-blue-500 transition-colors duration-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>

              {/* Input Box */}
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowDropdown(e.target.value.length > 0);
                }}
                onFocus={() => {
                  if (searchQuery.length > 0) setShowDropdown(true);
                }}
                placeholder="Search by service name (e.g., Cloud Sync, Verification)..."
                className="w-full pl-12 pr-32 py-3.5 bg-white/70 border border-slate-200/80 rounded-2xl text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:bg-white transition-all duration-300 shadow-sm text-sm font-medium"
              />

              {/* Action Button */}
              <button
                type="button"
                className="absolute right-1.5 top-1.5 bottom-1.5 px-6 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold text-xs sm:text-sm rounded-xl shadow-sm shadow-blue-500/10 active:scale-[0.98] transition-all duration-200 flex items-center justify-center"
              >
                {loading ? <Loader2 className="animate-spin" /> : "Search"}
              </button>
            </div>

            {/*  Results Dropdown Container */}
            {showDropdown && (
              <div className="absolute top-full left-0 right-0 mt-3 backdrop-blur-xl bg-white/90 border border-white/80 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.06)] overflow-hidden z-[9999] animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="px-4 py-2 bg-slate-50/50 border-b flex justify-between border-slate-100 text-[11px] font-bold tracking-wider text-slate-400 uppercase">
                  Matching Records{" "}
                  <button onClick={() => setShowDropdown(false)}>
                    <X className="hover:text-green-600" />
                  </button>
                </div>
                <ul className="divide-y divide-slate-100 max-h-72 overflow-y-auto custom-scrollbar">
                  {results?.map((service) => (
                    <li
                      key={service.id}
                      onClick={() => {
                        setSelectedService(service);
                        setShowDropdown(false);
                      }}
                      className="px-5 py-3.5 hover:bg-slate-50/80 transition-colors duration-150 cursor-pointer flex items-center justify-between group/item"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200/40">
                          {service.id}
                        </span>
                        <div className="flex flex-col">
                          <span className="font-semibold text-sm text-slate-700 group-hover/item:text-blue-600 transition-colors duration-150">
                            {service.service_name}
                          </span>
                          <span className="text-xs text-slate-400 mt-0.5">
                            {service.service_code}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200/40">
                          {service.adjustment_type}
                        </span>
                        <svg
                          className="w-4 h-4 text-slate-300 opacity-0 group-hover/item:opacity-100 group-hover/item:text-blue-500 transition-all duration-200 transform translate-x-[-4px] group-hover/item:translate-x-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2.5"
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content Body Area */}
      <main className="w-full mx-auto px-4 pb-12 relative ">
        {selectedSevice && (
          <div className="w-full px-4 md:px-8 pt-8 pb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Header Content */}
            <div className="space-y-1 max-w-4xl">
              <div className="flex items-center gap-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                <Server className="w-3.5 h-3.5" />
                <span>Service Report</span>
              </div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900 tracking-tight break-all">
                Results For{" "}
                <span className="text-blue-600 font-extrabold font-mono text-[90%] bg-blue-50/50 px-2 py-0.5 rounded-md border border-blue-100/30">
                  {selectedSevice?.service_name}
                </span>
              </h1>

              <div>
                {" "}
                <div className="flex gap-3 items-center ">
                  <span>Search using user id, company name, email</span>
                  <input className="bg-white/70 border rounded-2xl text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-4 focus:ring-blue-500/10 border-blue-500 focus:bg-white transition-all px-4 py-2 duration-300 shadow-sm text-sm font-medium" onChange={handleFilter} placeholder="Eg. APID1234" type="search" />
                </div>
              </div>
            </div>

            {/* Elegant Loading State */}
            {userLoading && (
              <div className="inline-flex items-center gap-2.5 px-4 py-2 bg-gray-50 rounded-xl border border-gray-200/60 self-start sm:self-center shadow-sm">
                <LoaderPinwheel
                  className="w-4 h-4 text-blue-600 animate-spin"
                  strokeWidth={2.5}
                />
                <span className="text-xs font-semibold text-gray-600 tracking-wide">
                  Syncing data...
                </span>
              </div>
            )}
          </div>
        )}

        {selectedSevice && userList && (
          <div>
            {userList.length > 0 && (
              <div className="w-full px-4 md:px-8 py-6">
                
            <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 sm:px-6">
              <div className="flex flex-1 justify-between sm:hidden">
                {/* Simple mobile view */}
                <button
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                  className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  disabled={page === pagination?.last_page}
                  onClick={() => setPage(page + 1)}
                  className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>

              {/* Desktop view */}
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                  <p className="text-sm text-gray-700">
                    Showing page{" "}
                    <span className="font-semibold text-gray-900">
                      {pagination?.current_page || 1}
                    </span>{" "}
                    of{" "}
                    <span className="font-semibold text-gray-900">
                      {pagination?.last_page || 1}
                    </span>
                  </p>
                  <span className="text-2xl">|</span>
                    <p className="text-sm text-gray-700">
                    Number Of Records  <span className="font-semibold text-gray-900">
                      {pagination?.total || "N/A"}
                    </span>{" "}
                    </p>
                </div>
                <div>
                  <nav
                    className="isolate inline-flex -space-x-px rounded-md shadow-xs"
                    aria-label="Pagination"
                  >
                    <button
                      disabled={page === 1}
                      onClick={() => setPage(page - 1)}
                      className="relative inline-flex items-center rounded-l-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-20 disabled:opacity-40 disabled:hover:bg-white disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      disabled={page === pagination?.last_page}
                      onClick={() => setPage(page + 1)}
                      className="relative inline-flex items-center rounded-r-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-20 disabled:opacity-40 disabled:hover:bg-white disabled:cursor-not-allowed"
                    >
                      {userLoading ? (
                        <Loader2 className="animate-spin" />
                      ) : (
                        "Next"
                      )}
                    </button>
                  </nav>
                </div>
              </div>
            </div>
                <div className="w-full overflow-x-auto rounded-2xl border border-gray-100 bg-white shadow-xl shadow-gray-200/40">
                  <table className="w-full min-w-[1000px] text-sm text-left text-gray-600">
                    <thead className="bg-gray-50/80 text-xs font-semibold uppercase tracking-wider text-gray-500 backdrop-blur-sm sticky top-0 ">
                      <tr>
                        <th className="px-6 py-5 rounded-tl-2xl">User ID</th>
                        <th className="px-6 py-5">Company Name</th>
                        <th className="px-6 py-5">Authorised Signatory</th>
                        <th className="px-6 py-5">Environment</th>
                        <th className="px-6 py-5">Email</th>
                        <th className="px-6 py-5">Mobile</th>
                        <th className="px-6 py-5">Service Name</th>
                        <th className="px-6 py-5">Status</th>
                        <th className="px-6 py-5">Scope</th>
                        <th className="px-6 py-5">Request Count</th>
                        <th className="px-6 py-5 rounded-tr-2xl">
                          Assigned On
                        </th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-100 bg-white">
                      {userList?.map((item, index) => (
                        <tr
                          key={index}
                          className="transition-colors duration-200 hover:bg-blue-50/50 group"
                        >
                          <td className="px-6 py-4 font-semibold text-gray-900 whitespace-nowrap">
                            {item.user_id}
                          </td>
                          <td className="px-6 py-4 font-medium text-gray-800 whitespace-nowrap">
                            {item.company_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {item.authorised_signatory}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium tracking-wide ${
                                item.environment === "UAT"
                                  ? "bg-amber-100 text-amber-800"
                                  : item.environment === "PRODUCTION"
                                    ? "bg-green-100 text-green-800"
                                    : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {item.environment}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {item.email}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {item.mobile}
                          </td>
                          <td
                            className="px-6 py-4 text-xs font-mono text-gray-500 max-w-[200px] truncate"
                            title={item.service_name}
                          >
                            {item.service_name}
                          </td>
                          <td
                            className="px-6 py-4 text-xs font-mono text-gray-500 max-w-[200px] truncate"
                            title={"status"}
                          >
                            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              ACTIVE ●
                            </span>
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700">
                              {item.scope}
                            </span>
                          </td>
                          <td className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap">
                            {item.request_count}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                            {item.assigned_on}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

          </div>
        )}
        {userList?.length === 0 && selectedSevice && (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center bg-gray-50/40 rounded-2xl border border-dashed border-gray-200/80 my-6 max-w-xl mx-auto backdrop-blur-sm">
            {/* Lucide Icon Wrapper */}
            <div className="flex items-center justify-center w-12 h-12 bg-white rounded-xl shadow-sm border border-gray-100 mb-4">
              <Inbox className="w-5 h-5 text-gray-400" strokeWidth={1.5} />
            </div>

            {/* Typography */}
            <h3 className="text-sm font-semibold text-gray-900 tracking-wide">
              No Data Available
            </h3>
            <p className="mt-1 text-xs text-gray-400 max-w-xs leading-relaxed">
              There are currently no records found matching your selected
              service configuration.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}

export default GetUsersByServiceId;
